<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import QRCode from 'qrcode'
import { extractError } from '@/api/client'
import Modal from '@/components/ui/Modal.vue'

const props = defineProps<{
  open: boolean
  data: string
  title?: string
}>()

const emit = defineEmits<{ close: [] }>()

const imgSrc = ref('')
const loadError = ref(false)
const loading = ref(false)
const errorDetail = ref('')
let gen = 0

async function loadQr(data: string) {
  const my = ++gen
  loadError.value = false
  errorDetail.value = ''
  loading.value = true
  imgSrc.value = ''
  try {
    // Generate in-browser — SSH JSON+PEM is too large / flaky for /api/qr-code GET+POST.
    const url = await QRCode.toDataURL(data, {
      errorCorrectionLevel: 'L',
      width: 320,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
    if (my !== gen) return
    imgSrc.value = url
  } catch (e) {
    if (my !== gen) return
    loadError.value = true
    errorDetail.value = extractError(e, 'Не удалось построить QR')
    imgSrc.value = ''
  } finally {
    if (my === gen) loading.value = false
  }
}

watch(
  () => [props.open, props.data] as const,
  ([open, data]) => {
    if (!open || !data) {
      gen++
      imgSrc.value = ''
      loadError.value = false
      errorDetail.value = ''
      loading.value = false
      return
    }
    void loadQr(data)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  gen++
})
</script>

<template>
  <Modal
    :open="open"
    :title="title || 'QR код'"
    max-width="sm"
    @close="emit('close')"
  >
    <div class="flex flex-col items-center gap-3">
      <img
        v-if="imgSrc && !loadError"
        :src="imgSrc"
        alt="QR код"
        class="max-w-full rounded border border-white-light"
        @error="loadError = true; errorDetail = errorDetail || 'Не удалось показать QR'"
      />
      <div v-else-if="loading" class="text-sm text-muted">Генерация QR…</div>
      <div v-else class="alert alert-danger w-full">
        Ошибка загрузки QR
        <div v-if="errorDetail" class="mt-1 text-xs opacity-80">{{ errorDetail }}</div>
        <div v-else class="mt-1 text-xs opacity-80">Конфиг слишком большой для QR или пустой</div>
      </div>
      <p class="text-sm text-muted">Отсканируйте QR код для подключения</p>
    </div>
  </Modal>
</template>
